// Capa HTTP y lógica pura de la fase 3 de la generación del informe: las
// secciones, su redacción con IA, las citas `[[valor|clave]]` y su verificación.
//
// El estado vive en TanStack Query (`hooks/useRedaccion.ts`); acá van los tipos
// del contrato (6a), los fetch y las reglas que se prueban sin montar nada.
//
// LAS CIFRAS NO SE CALCULAN ACÁ. El redactor las CITA, el servidor las compara
// contra la hoja de hechos y esta capa solo pinta el resultado: decide qué
// cita se ve verde, ámbar o roja a partir de lo que el verificador ya dijo.

import { ApiError, apiFetch } from "./api";
import { formatearFechaYHora } from "./fechas";
import type { DestinoDeLaCorreccion, Hecho } from "./generacion";
import type { TonoDelCarril } from "./generacion";

/* ---------------------------------------------------------------------------
   Contrato del API (6a). Los nombres son los del servidor.
--------------------------------------------------------------------------- */

export type EstadoDeRedaccion = "pendiente" | "generando" | "propuesta" | "editada" | "validada" | "error";

export type ModoDeProduccion =
  | "sistema"
  | "tabla"
  | "calculo"
  | "texto_fijo"
  | "texto_validado"
  | "redactado_ia"
  | "imagen_anexo";

export interface ErrorDeVerificacion {
  tipo: string;
  mensaje: string;
  /** La clave de la cita a la que se refiere, cuando se refiere a una. */
  cita?: string;
  /** Lo que escribió el redactor entre `[[` y `|`. */
  citado?: string;
  /** Lo que dice la hoja de hechos (solo `valor_distinto`). */
  esperado?: string;
  fragmento?: string;
}

export interface Verificacion {
  estado: "ok" | "advertencias" | "errores";
  errores: ErrorDeVerificacion[];
  advertencias: ErrorDeVerificacion[];
  /** Claves de cita válidas que el texto menciona. */
  citas: string[];
}

export interface PersonaDeLaSeccion {
  id: number;
  name: string | null;
}

export interface Redaccion {
  id: number;
  estado: EstadoDeRedaccion;
  texto: string | null;
  desactualizada: boolean;
  motivo: string | null;
  motivos: string[];
  citas: string[];
  verificacion: Verificacion | null;
  faltantes: string[];
  fuentes: string[];
  validada_por: PersonaDeLaSeccion | null;
  validada_en: string | null;
  mantenida_por: PersonaDeLaSeccion | null;
  mantenida_en: string | null;
  error: { codigo: string; mensaje: string } | null;
  revisiones: number;
  ultima_revision: {
    id: number;
    autor_tipo: AutorDeRevision;
    autor: PersonaDeLaSeccion | null;
    creada_en: string | null;
    actualizada_en: string | null;
  } | null;
  prompt: { funcion: string; revision_id: number | null } | null;
  corrida_id: number | null;
}

export interface SeccionDelInforme {
  item_id: number;
  nombre: string;
  grupo: string | null;
  subgrupo: string | null;
  descripcion: string | null;
  orden: number;
  modo: ModoDeProduccion | null;
  modo_etiqueta: string | null;
  modo_confirmado: boolean;
  se_redacta_con_ia: boolean;
  es_final: boolean;
  dependencias: { item_id: number; nombre: string; estado: EstadoDeRedaccion | null }[];
  /** La prosa de la pestaña, para `texto_validado`. */
  origen: { seccion: string; texto: string; validada: boolean } | null;
  redaccion: Redaccion | null;
}

export interface FaltaParaLasFinales {
  item_id: number;
  nombre: string;
  motivo: "desactualizada" | "sin_validar" | "pendiente";
}

export interface ResumenDeSecciones {
  total: number;
  redactables: number;
  pendientes: number;
  en_curso: number;
  propuestas: number;
  editadas: number;
  validadas: number;
  con_error: number;
  desactualizadas: number;
  finales_habilitadas: boolean;
  finales_faltan: FaltaParaLasFinales[];
}

/** Por qué la IA no puede redactar todavía: lo decide el servidor (`PuertaDeLaRedaccion`). */
export interface MotivoDeRedaccionCerrada {
  codigo: "sin_analisis" | "dudas_sin_responder" | "correcciones_pendientes";
  mensaje: string;
  cantidad: number;
  /** Con `dudas_sin_responder`, las dudas; con `correcciones_pendientes`, ver `correcciones`. */
  detalle: { id: number; corto: string; gravedad: "bloquea" | "advierte" }[] | CorreccionPendienteDeLaPuerta[];
}

/** Una respuesta que dijo cuál debe ser un dato de una pestaña y la hoja aún no lo muestra. */
export interface CorreccionPendienteDeLaPuerta {
  duda_id: number;
  corto: string;
  clave: string;
  sujeto: string;
  campo: string;
  valor_esperado: string | null;
  valor_actual: string | null;
  destino: DestinoDeLaCorreccion;
}

/** Las correcciones pendientes que cierran la redacción, si las hay. */
export function correccionesDeLaPuerta(puerta: PuertaDeLaRedaccion): CorreccionPendienteDeLaPuerta[] {
  const motivo = puerta.motivos.find((m) => m.codigo === "correcciones_pendientes");

  return motivo ? (motivo.detalle as CorreccionPendienteDeLaPuerta[]) : [];
}

export interface PuertaDeLaRedaccion {
  puede_redactar: boolean;
  motivos: MotivoDeRedaccionCerrada[];
}

export interface DatosDeSecciones {
  secciones: SeccionDelInforme[];
  resumen: ResumenDeSecciones;
  /** Ausente = abierta (una respuesta anterior a la puerta no la cierra). */
  puerta?: PuertaDeLaRedaccion;
}

/** `true` si la IA no puede redactar ahora; editar, validar y reabrir siguen abiertos. */
export const redaccionCerrada = (datos: DatosDeSecciones): boolean => datos.puerta?.puede_redactar === false;

export interface CorridaDeLaRedaccion {
  corrida_id: number;
  tipo: string;
  estado: "en_curso" | "ok" | "error";
  porcentaje: number;
  nivel: { actual: number; total: number };
  contadores: {
    secciones: number;
    en_cola: number;
    redactadas: number;
    con_error: number;
    omitidas: number;
    llamadas_al_modelo: number;
  };
  secciones: {
    item_id: number;
    redaccion_id: number;
    nombre: string;
    estado: EstadoDeRedaccion | null;
    error: { codigo: string; mensaje: string } | null;
    omitida: string | null;
  }[];
  pasos: { nombre: string; estado: string; iniciado_en: string | null; terminado_en: string | null }[];
  iniciada_en: string | null;
  terminada_en: string | null;
  error: { codigo: string; mensaje: string } | null;
}

export type AutorDeRevision = "persona" | "ia" | "sistema";

export interface RevisionDelHistorial {
  id: number;
  autor_tipo: AutorDeRevision;
  autor: PersonaDeLaSeccion | null;
  nombre: string | null;
  origen: Record<string, unknown> | null;
  creada_en: string | null;
  actualizada_en: string | null;
  palabras_agregadas: number;
  palabras_quitadas: number;
  vigente: boolean;
}

export interface TramoDelDiff {
  tipo: "same" | "added" | "removed";
  texto: string;
  palabras: number;
}

export interface ComparacionDeRevisiones {
  a: number;
  b: number;
  agregadas: number;
  quitadas: number;
  cambios: number;
  segmentos: TramoDelDiff[];
}

/* ---------------------------------------------------------------------------
   HTTP
--------------------------------------------------------------------------- */

export const INTERVALO_DE_POLL_DE_LA_REDACCION_MS = 3000;

/** Sondea mientras la corrida está abierta; al terminar TanStack apaga el intervalo. */
export function intervaloDeLaRedaccion(corrida: CorridaDeLaRedaccion | null | undefined): number | false {
  return corrida?.estado === "en_curso" ? INTERVALO_DE_POLL_DE_LA_REDACCION_MS : false;
}

const base = (informeId: number) => `/api/informes/${informeId}`;
const deLaSeccion = (informeId: number, itemId: number) => `${base(informeId)}/secciones/${itemId}`;

export async function fetchSecciones(informeId: number): Promise<DatosDeSecciones> {
  const r = await apiFetch<{ data: DatosDeSecciones }>(`${base(informeId)}/secciones`);
  return r.data;
}

export async function fetchCorridaDeLaRedaccion(informeId: number): Promise<CorridaDeLaRedaccion | null> {
  const r = await apiFetch<{ data: CorridaDeLaRedaccion | null }>(`${base(informeId)}/redaccion`);
  return r.data;
}

export async function redactarPendientesRequest(informeId: number): Promise<CorridaDeLaRedaccion> {
  const r = await apiFetch<{ data: CorridaDeLaRedaccion }>(`${base(informeId)}/redaccion`, { method: "POST" });
  return r.data;
}

export async function redactarFinalesRequest(informeId: number): Promise<CorridaDeLaRedaccion> {
  const r = await apiFetch<{ data: CorridaDeLaRedaccion }>(`${base(informeId)}/redaccion/finales`, { method: "POST" });
  return r.data;
}

export async function regenerarRequest(
  informeId: number,
  itemId: number,
  instruccion: string | null,
): Promise<CorridaDeLaRedaccion> {
  const r = await apiFetch<{ data: CorridaDeLaRedaccion }>(`${deLaSeccion(informeId, itemId)}/regenerar`, {
    method: "POST",
    body: JSON.stringify(instruccion === null || instruccion.trim() === "" ? {} : { instruccion: instruccion.trim() }),
  });
  return r.data;
}

export async function editarTextoRequest(informeId: number, itemId: number, texto: string): Promise<SeccionDelInforme> {
  const r = await apiFetch<{ data: SeccionDelInforme }>(`${deLaSeccion(informeId, itemId)}/texto`, {
    method: "PUT",
    body: JSON.stringify({ texto }),
  });
  return r.data;
}

export type AccionDeLaSeccion = "validar" | "reabrir" | "mantener";

export async function accionDeLaSeccionRequest(
  informeId: number,
  itemId: number,
  accion: AccionDeLaSeccion,
): Promise<SeccionDelInforme> {
  const r = await apiFetch<{ data: SeccionDelInforme }>(`${deLaSeccion(informeId, itemId)}/${accion}`, {
    method: "POST",
  });
  return r.data;
}

export async function fetchRevisiones(informeId: number, itemId: number): Promise<RevisionDelHistorial[]> {
  const r = await apiFetch<{ data: RevisionDelHistorial[] }>(`${deLaSeccion(informeId, itemId)}/revisiones`);
  return r.data;
}

export async function fetchComparacion(
  informeId: number,
  itemId: number,
  a: number,
  b: number,
): Promise<ComparacionDeRevisiones> {
  const r = await apiFetch<{ data: ComparacionDeRevisiones }>(
    `${deLaSeccion(informeId, itemId)}/revisiones/comparar?a=${a}&b=${b}`,
  );
  return r.data;
}

/* ---------------------------------------------------------------------------
   Errores de las acciones
--------------------------------------------------------------------------- */

export interface ErrorDeLaAccion {
  /** `redaccion_en_curso`, `nada_que_redactar`, `finales_bloqueadas`… o `null` si el cuerpo no lo trae. */
  codigo: string | null;
  mensaje: string | null;
  faltan: FaltaParaLasFinales[];
}

/** Lee el cuerpo `{codigo, message, faltan?}` de un 409/422 sin asumir que lo trae. */
export function errorDeLaAccion(error: unknown): ErrorDeLaAccion {
  const vacio: ErrorDeLaAccion = { codigo: null, mensaje: null, faltan: [] };
  if (!(error instanceof ApiError)) return vacio;

  try {
    const cuerpo = JSON.parse(error.body) as { codigo?: string; message?: string; faltan?: FaltaParaLasFinales[] };

    return { codigo: cuerpo.codigo ?? null, mensaje: cuerpo.message ?? null, faltan: cuerpo.faltan ?? [] };
  } catch {
    return vacio;
  }
}

/* ---------------------------------------------------------------------------
   Etiquetas
--------------------------------------------------------------------------- */

export const ETIQUETA_ESTADO_DE_REDACCION: Record<EstadoDeRedaccion, string> = {
  pendiente: "Pendiente",
  generando: "Redactando",
  propuesta: "Por revisar",
  editada: "Editada",
  validada: "Validada",
  error: "Con error",
};

export const VARIANTE_ESTADO_DE_REDACCION: Record<
  EstadoDeRedaccion,
  "outline" | "info" | "warning" | "success" | "destructive"
> = {
  pendiente: "outline",
  generando: "info",
  propuesta: "warning",
  editada: "warning",
  validada: "success",
  error: "destructive",
};

/** Cómo se produce una sección que NO redacta la IA, dicho para quien revisa. */
export const DESCRIPCION_DEL_MODO: Record<Exclude<ModoDeProduccion, "redactado_ia">, string> = {
  sistema: "El sistema lo completa con los datos del expediente. No hay texto que revisar acá.",
  tabla: "Se arma como tabla con los datos del expediente. Se corrige en la pestaña correspondiente.",
  calculo: "Lo calcula el sistema a partir de la valorización y la hoja de hechos.",
  texto_fijo: "Es un texto fijo de la plantilla: sale igual en todos los informes de este tipo.",
  texto_validado: "Usa la prosa que ya se validó en la pestaña del expediente; la IA no la reescribe.",
  imagen_anexo: "Es una imagen o un anexo del expediente. No lleva texto redactado.",
};

export const ETIQUETA_DE_MOTIVO_DE_LAS_FINALES: Record<FaltaParaLasFinales["motivo"], string> = {
  desactualizada: "validada, pero desactualizada",
  sin_validar: "redactada, sin validar",
  pendiente: "todavía sin redactar",
};

/* ---------------------------------------------------------------------------
   Citas: [[valor|clave]]
--------------------------------------------------------------------------- */

export type SegmentoDeTexto =
  | { tipo: "texto"; texto: string }
  | { tipo: "cita"; valor: string; clave: string | null };

const MARCADOR_DE_CITA = /\[\[([^\]\n]*)\]\]/g;

/** `valor|clave`: la barra que separa es la ÚLTIMA, igual que en el servidor. */
function partirCita(interior: string): { valor: string; clave: string | null } {
  const barra = interior.lastIndexOf("|");
  if (barra === -1) return { valor: interior.trim(), clave: null };

  return { valor: interior.slice(0, barra).trim(), clave: interior.slice(barra + 1).trim() || null };
}

/** Parte un texto en prosa y citas. Una cita sin clave sigue siendo una cita (se pinta en rojo). */
export function dividirEnCitas(texto: string): SegmentoDeTexto[] {
  const segmentos: SegmentoDeTexto[] = [];
  let desde = 0;

  for (const coincidencia of texto.matchAll(MARCADOR_DE_CITA)) {
    const inicio = coincidencia.index ?? 0;
    if (inicio > desde) segmentos.push({ tipo: "texto", texto: texto.slice(desde, inicio) });

    segmentos.push({ tipo: "cita", ...partirCita(coincidencia[1]) });
    desde = inicio + coincidencia[0].length;
  }

  if (desde < texto.length) segmentos.push({ tipo: "texto", texto: texto.slice(desde) });

  return segmentos;
}

/** El texto tal como lo lee el cliente: cada cita reemplazada por su valor. */
export function textoSinMarcas(texto: string): string {
  return dividirEnCitas(texto)
    .map((s) => (s.tipo === "texto" ? s.texto : s.valor))
    .join("");
}

/** `clave@sujeto` comparable: minúsculas, sin tildes y con los espacios colapsados. */
function normalizarClave(clave: string): string {
  return clave
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

export type EstadoDeLaCita = "ok" | "no_cuadra" | "desconocida";

export interface CitaResuelta {
  estado: EstadoDeLaCita;
  hecho: Hecho | null;
  /** Lo que dice la hoja cuando la cita no cuadra. */
  esperado: string | null;
  /** Por qué es desconocida, con las palabras del servidor cuando las hay. */
  mensaje: string | null;
}

const ERRORES_DE_CITA_DESCONOCIDA = new Set(["clave_desconocida", "hecho_inexistente", "cita_malformada"]);

/**
 * Qué se ve de una cita: la resuelve contra lo que el VERIFICADOR ya dijo y la
 * hoja de hechos. No compara valores por su cuenta: la normalización (miles,
 * "ha"/"há", símbolos) vive en el servidor y duplicarla acá haría que la
 * pantalla y el guardado discreparan.
 */
export function resolverCita(
  cita: Extract<SegmentoDeTexto, { tipo: "cita" }>,
  verificacion: Verificacion | null,
  hechos: Hecho[],
): CitaResuelta {
  if (cita.clave === null) {
    return { estado: "desconocida", hecho: null, esperado: null, mensaje: "La cita no dice a qué hecho de la hoja se refiere." };
  }

  const clave = normalizarClave(cita.clave);
  const hecho = hechos.find((h) => normalizarClave(h.cita) === clave) ?? null;

  const delVerificador = (verificacion?.errores ?? []).find(
    (e) => e.cita !== undefined && normalizarClave(e.cita) === clave && (e.citado === undefined || e.citado.trim() === cita.valor),
  );

  if (delVerificador?.tipo === "valor_distinto") {
    return { estado: "no_cuadra", hecho, esperado: delVerificador.esperado ?? hecho?.valor_formateado ?? null, mensaje: delVerificador.mensaje };
  }

  if (delVerificador !== undefined && ERRORES_DE_CITA_DESCONOCIDA.has(delVerificador.tipo)) {
    return { estado: "desconocida", hecho, esperado: null, mensaje: delVerificador.mensaje };
  }

  // Sin una palabra del verificador, solo se acusa lo que la hoja cargada
  // demuestra que no existe: sin hoja no hay con qué contradecir.
  if (hecho === null && hechos.length > 0) {
    return { estado: "desconocida", hecho: null, esperado: null, mensaje: `«${cita.clave}» no está en la hoja de hechos.` };
  }

  return { estado: "ok", hecho, esperado: null, mensaje: null };
}

/* ---------------------------------------------------------------------------
   Verificación automática: una fila por cita y una por cada otro hallazgo
--------------------------------------------------------------------------- */

export interface FilaDeVerificacion {
  nivel: "ok" | "aviso" | "error";
  /** El valor citado, cuando la fila es de una cita. */
  valor: string | null;
  mensaje: string;
}

const HALLAZGOS_DE_UNA_CITA = new Set([
  "valor_distinto",
  "clave_desconocida",
  "hecho_inexistente",
  "cita_malformada",
  "hecho_sin_valor",
  "cita_de_texto",
  "hecho_con_duda",
  "hecho_sin_confirmar",
]);

export function filasDeLaVerificacion(texto: string, verificacion: Verificacion | null, hechos: Hecho[]): FilaDeVerificacion[] {
  const filas: FilaDeVerificacion[] = [];
  const vistas = new Set<string>();
  const claves = new Set<string>();

  for (const segmento of dividirEnCitas(texto)) {
    if (segmento.tipo !== "cita") continue;

    const huella = `${segmento.valor}|${segmento.clave}`;
    if (vistas.has(huella)) continue;
    vistas.add(huella);
    if (segmento.clave !== null) claves.add(normalizarClave(segmento.clave));

    const r = resolverCita(segmento, verificacion, hechos);

    if (r.estado === "no_cuadra") {
      filas.push({ nivel: "error", valor: segmento.valor, mensaje: `No cuadra: la hoja de hechos dice ${r.esperado ?? "otra cosa"}` });
    } else if (r.estado === "desconocida") {
      filas.push({ nivel: "error", valor: segmento.valor, mensaje: r.mensaje ?? "La cita no existe en la hoja de hechos." });
    } else {
      const aviso = (verificacion?.advertencias ?? []).find(
        (a) => a.cita !== undefined && segmento.clave !== null && normalizarClave(a.cita) === normalizarClave(segmento.clave),
      );

      filas.push(
        aviso !== undefined
          ? { nivel: "aviso", valor: segmento.valor, mensaje: aviso.mensaje }
          : { nivel: "ok", valor: segmento.valor, mensaje: r.hecho ? `Coincide con «${r.hecho.nombre}»` : "Coincide con la hoja de hechos" },
      );
    }
  }

  // Lo que no cuelga de una cita que se ve en el texto: cifras sueltas,
  // repeticiones, marcadores sin resolver, texto vacío…
  const sueltos = (lista: ErrorDeVerificacion[], nivel: "error" | "aviso") =>
    lista
      .filter((e) => !(HALLAZGOS_DE_UNA_CITA.has(e.tipo) && e.cita !== undefined && claves.has(normalizarClave(e.cita))))
      .map((e): FilaDeVerificacion => ({ nivel, valor: e.fragmento ?? null, mensaje: e.mensaje }));

  return [...filas, ...sueltos(verificacion?.errores ?? [], "error"), ...sueltos(verificacion?.advertencias ?? [], "aviso")];
}

/* ---------------------------------------------------------------------------
   Diff de dos versiones, sin las marcas de las citas
--------------------------------------------------------------------------- */

export interface TramoLegible {
  tipo: TramoDelDiff["tipo"];
  texto: string;
}

/**
 * El diff por palabras viene sobre el texto CRUDO, con `[[valor|clave]]` partido
 * en pedazos de distinto tipo. Se aplana a caracteres, se localizan las marcas
 * sobre el texto completo y se descartan SOLO sus signos (`[[` y `|clave]]`):
 * el valor conserva si se agregó o se quitó, que es lo que importa ver.
 */
export function segmentosLegiblesDelDiff(segmentos: (Pick<TramoDelDiff, "tipo" | "texto"> & { palabras?: number })[]): TramoLegible[] {
  const letras: { c: string; tipo: TramoDelDiff["tipo"] }[] = [];
  for (const s of segmentos) for (const c of Array.from(s.texto)) letras.push({ c, tipo: s.tipo });

  const plano = letras.map((l) => l.c).join("");
  const descartar = new Set<number>();
  // Los índices de matchAll son de UTF-16, no de caracteres: se trabaja con
  // posiciones de `Array.from` para que un emoji en el texto no corra la cuenta.
  const posicion = (indiceUtf16: number) => Array.from(plano.slice(0, indiceUtf16)).length;

  for (const m of plano.matchAll(MARCADOR_DE_CITA)) {
    const inicio = posicion(m.index ?? 0);
    const largo = Array.from(m[0]).length;
    const interior = Array.from(m[1]);
    const barra = interior.lastIndexOf("|");

    descartar.add(inicio);
    descartar.add(inicio + 1);
    for (let i = barra === -1 ? interior.length : barra; i < interior.length; i++) descartar.add(inicio + 2 + i);
    descartar.add(inicio + largo - 2);
    descartar.add(inicio + largo - 1);
  }

  const resultado: TramoLegible[] = [];
  letras.forEach((l, i) => {
    if (descartar.has(i)) return;

    const ultimo = resultado[resultado.length - 1];
    if (ultimo?.tipo === l.tipo) ultimo.texto += l.c;
    else resultado.push({ tipo: l.tipo, texto: l.c });
  });

  return resultado;
}

/* ---------------------------------------------------------------------------
   Secciones: orden, filtros y "siguiente por revisar"
--------------------------------------------------------------------------- */

/** Lo que una persona tiene que mirar: lo propuesto o editado, y lo validado que quedó desactualizado. */
export function necesitaRevision(seccion: SeccionDelInforme): boolean {
  const r = seccion.redaccion;
  if (!seccion.se_redacta_con_ia || r === null) return false;

  return r.estado === "propuesta" || r.estado === "editada" || (r.estado === "validada" && r.desactualizada);
}

/**
 * La siguiente sección por revisar DESPUÉS de `itemIdActual`, en el orden en
 * que llega la lista (el de la plantilla), dando la vuelta; nunca la misma.
 */
export function siguientePorRevisar(secciones: SeccionDelInforme[], itemIdActual: number): SeccionDelInforme | null {
  const posicion = secciones.findIndex((s) => s.item_id === itemIdActual);
  const ordenadas = [...secciones.slice(posicion + 1), ...secciones.slice(0, Math.max(posicion, 0))];

  return ordenadas.find((s) => s.item_id !== itemIdActual && necesitaRevision(s)) ?? null;
}

export interface GrupoDeSecciones {
  grupo: string | null;
  subgrupos: { subgrupo: string | null; secciones: SeccionDelInforme[] }[];
}

/** Capítulo › subcapítulo, en el orden de PRIMERA aparición: el de la plantilla. */
export function agruparSecciones(secciones: SeccionDelInforme[]): GrupoDeSecciones[] {
  const grupos: GrupoDeSecciones[] = [];

  for (const seccion of secciones) {
    let grupo = grupos.find((g) => g.grupo === seccion.grupo);
    if (grupo === undefined) {
      grupo = { grupo: seccion.grupo, subgrupos: [] };
      grupos.push(grupo);
    }

    let sub = grupo.subgrupos.find((s) => s.subgrupo === seccion.subgrupo);
    if (sub === undefined) {
      sub = { subgrupo: seccion.subgrupo, secciones: [] };
      grupo.subgrupos.push(sub);
    }

    sub.secciones.push(seccion);
  }

  return grupos;
}

const GRUPOS_ESTRUCTURALES = ["portada", "contenido"];

export interface NumeroDeSeccion {
  /** «2.1», como lo numera el documento; `null` en Portada, Contenido y sin grupo. */
  id: string | null;
  /** «2. Descripción de la propiedad»; `null` si no es un capítulo. */
  capitulo: string | null;
}

/**
 * La numeración del documento aplicada a la lista: un capítulo por GRUPO (menos
 * Portada y Contenido, que dibuja el documento por su cuenta) y, dentro de él, un
 * número por subgrupo o por ítem sin subgrupo. Es el mismo criterio de
 * `EnsambladorDelInforme`, pero NO sabe del `orden_capitulos` del formato de
 * salida: esa reordenación solo la conoce el documento.
 *
 * Se calcula sobre la lista COMPLETA, nunca sobre la filtrada: «Ver todo el
 * informe» no puede cambiar el número de una sección.
 */
export function numerarSecciones(secciones: SeccionDelInforme[]): Map<number, NumeroDeSeccion> {
  const resultado = new Map<number, NumeroDeSeccion>();
  const capitulos: { grupo: string; numero: number; contador: number; subgrupo: string | null; id: string }[] = [];

  for (const seccion of secciones) {
    const grupo = seccion.grupo?.trim() ?? "";

    if (grupo === "" || GRUPOS_ESTRUCTURALES.includes(grupo.toLowerCase())) {
      resultado.set(seccion.item_id, { id: null, capitulo: null });
      continue;
    }

    let capitulo = capitulos.find((c) => c.grupo === grupo);
    if (capitulo === undefined) {
      capitulo = { grupo, numero: capitulos.length + 1, contador: 0, subgrupo: null, id: "" };
      capitulos.push(capitulo);
    }

    const subgrupo = seccion.subgrupo?.trim() ? seccion.subgrupo.trim() : null;

    if (subgrupo === null || subgrupo !== capitulo.subgrupo) {
      capitulo.contador += 1;
      capitulo.id = `${capitulo.numero}.${capitulo.contador}`;
    }
    capitulo.subgrupo = subgrupo;

    resultado.set(seccion.item_id, { id: capitulo.id, capitulo: `${capitulo.numero}. ${grupo}` });
  }

  return resultado;
}

export type TonoDeLaSeccion = "tenue" | "neutral" | "warning" | "info" | "success" | "danger" | "desactualizada";

/** El tono de la etiqueta de una sección en el árbol y en el encabezado de su panel. */
export function tonoDeLaSeccion(seccion: SeccionDelInforme): TonoDeLaSeccion {
  const r = seccion.redaccion;

  if (r === null) return "tenue";
  if (r.estado === "validada" && r.desactualizada) return "desactualizada";

  switch (r.estado) {
    case "pendiente":
      return "tenue";
    case "generando":
      return "neutral";
    case "propuesta":
      return "warning";
    case "editada":
      return "info";
    case "validada":
      return "success";
    case "error":
      return "danger";
  }
}

/**
 * La línea de metadatos bajo el título del panel: quién escribió la última
 * versión, cuándo, cuántas van y si ya la validó una persona. Solo dice lo que el
 * servidor sabe: sin revisión no hay autor ni fecha que inventar.
 */
export function metaDeLaRedaccion(r: Redaccion): string {
  const revision = r.ultima_revision;
  const partes: string[] = [];

  if (revision) {
    const quien =
      revision.autor_tipo === "ia"
        ? "Propuesta de la IA"
        : revision.autor?.name
          ? `Editada por ${revision.autor.name}`
          : "Editada a mano";
    partes.push(quien);

    const cuando = revision.actualizada_en ?? revision.creada_en;
    if (cuando) partes.push(formatearFechaYHora(cuando));
  }

  if (r.revisiones > 0) partes.push(`versión ${r.revisiones}`);
  partes.push(r.estado === "validada" ? "validada" : "sin validar");

  return partes.join(" · ");
}

export type FiltroDeSecciones = "por_revisar" | "redactadas_ia";

export const FILTROS_DE_SECCIONES: { filtro: FiltroDeSecciones; etiqueta: string }[] = [
  { filtro: "por_revisar", etiqueta: "Por revisar" },
  { filtro: "redactadas_ia", etiqueta: "Redactadas por IA" },
];

export interface VistaDeSecciones {
  filtro: FiltroDeSecciones | null;
  verTodo: boolean;
}

/**
 * Sin «Ver todo el informe» el árbol muestra lo que la persona puede hacer algo
 * con: lo que redacta la IA y lo que todavía no tiene modo confirmado. Las
 * demás (tablas, textos fijos…) se producen solas y estorban al revisar.
 */
export function filtrarSecciones(secciones: SeccionDelInforme[], vista: VistaDeSecciones): SeccionDelInforme[] {
  const visibles = vista.verTodo ? secciones : secciones.filter((s) => s.se_redacta_con_ia || !s.modo_confirmado);

  switch (vista.filtro) {
    case "por_revisar":
      return visibles.filter(necesitaRevision);
    case "redactadas_ia":
      return visibles.filter((s) => s.se_redacta_con_ia);
    case null:
      return visibles;
  }
}

/* ---------------------------------------------------------------------------
   Carril de fases y secciones del final
--------------------------------------------------------------------------- */

const plural = (n: number, singular: string, plural: string) => `${n} ${n === 1 ? singular : plural}`;

export function estadoDeLaFase3(datos: DatosDeSecciones | undefined): { tono: TonoDelCarril; texto: string } {
  if (datos === undefined) return { tono: "reposo", texto: "Sin cargar" };

  const r = datos.resumen;

  if (r.en_curso > 0) return { tono: "en_curso", texto: `Redactando · ${plural(r.en_curso, "sección", "secciones")}` };
  if (r.redactables === 0) return { tono: "reposo", texto: "Sin secciones por redactar" };

  const partes = [`${r.validadas} de ${r.redactables} validadas`];
  if (r.desactualizadas > 0) partes.push(plural(r.desactualizadas, "desactualizada", "desactualizadas"));
  if (r.con_error > 0) partes.push(`${r.con_error} con error`);
  const texto = partes.join(" · ");

  if (r.con_error > 0) return { tono: "error", texto };
  if (r.validadas === r.redactables && r.desactualizadas === 0) return { tono: "ok", texto };
  if (r.pendientes === r.redactables) return { tono: "reposo", texto };

  return { tono: "aviso", texto };
}

/** «N de M listas» de la tarjeta de las secciones del final. */
export function cuentaDeLasFinales(datos: DatosDeSecciones): { listas: number; total: number } {
  const total = datos.secciones.filter((s) => s.se_redacta_con_ia && !s.es_final).length;

  return { listas: Math.max(0, total - datos.resumen.finales_faltan.length), total };
}
